import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Calendar, ChevronRight, ClipboardCheck, ArrowLeft } from "lucide-react";
import WMSPMCheckSheet from "../documentation/WMSPMCheckSheet";
import InverterCheckSheet from "../documentation/InverterCheckSheet";
import PMCalendar from "./PMCalendar";
import "./styles/om-page.css";
import "../../../styles/om-module.css";

import { omOptions, OMType } from './OMSidebar';

const VALID_FORMS: OMType[] = ['module-cleaning', 'grass-cutting', 'daily', 'weekly', 'monthly', 'quarterly', 'yearly'];

const OMPage: React.FC = () => {
    const { site } = useParams<{ site: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [activeOM, setActiveOM] = useState<OMType>('selector');

    // Check for type query parameter and set active O&M form accordingly
    useEffect(() => {
        // The app launcher links with display names ("Daily Checklist"), the sidebar with ids ("daily")
        const type = (searchParams.get('form') || '').toLowerCase().trim().replace(/\s+/g, '-').replace(/-checklist$/, '');
        if (type && VALID_FORMS.includes(type as OMType)) {
            setActiveOM(type as OMType);
        } else {
            setActiveOM('selector');
        }
    }, [searchParams]);

    // Render the selected O&M form component without internal sidebar
    const selectedOption = activeOM !== 'selector' ? omOptions.find(opt => opt.id === activeOM) : null;

    return (
        <div
            className="om-root flex flex-col min-h-screen bg-gray-50"
            style={{
                fontFamily: "'IBM Plex Sans', -apple-system, sans-serif",
                paddingTop: '64px',
            }}
        >
            {/* Main Content */}
            <div className="flex-1 w-full min-w-0 transition-all duration-300">
                {activeOM !== 'selector' ? (
                    <>
                        {/* Back navigation header for checklist sheets */}
                        <div
                            className="bg-white border-b border-[#DFE6EE] px-4 sm:px-6 py-2.5 flex items-center justify-between"
                            style={{ position: 'sticky', top: '64px', zIndex: 20 }}
                        >
                            <button
                                type="button"
                                onClick={() => navigate(`/om/${site}`)}
                                className="inline-flex items-center gap-2 text-xs font-semibold text-[#0084CF] hover:text-[#0066A3] transition-colors"
                            >
                                <ArrowLeft size={14} />
                                Back to PM Calendar
                            </button>
                            <span className="text-xs text-slate-500 font-medium">
                                Operations &amp; Maintenance &middot; {selectedOption?.title || 'Checklist'}
                            </span>
                        </div>

                        {/* Render WMS PM Check Sheet for Weekly */}
                        {activeOM === 'weekly' ? (
                            <div>
                                <WMSPMCheckSheet />
                            </div>
                        ) : activeOM === 'monthly' ? (
                            /* Render Inverter Check Sheet for Monthly */
                            <div>
                                <InverterCheckSheet />
                            </div>
                        ) : (
                            /* Forms that are not yet implemented */
                            <div className="omx min-h-screen py-6" style={{ background: '#F6F8FB' }}>
                                <div className="omx-shell">
                                    <div className="omx-pagehead">
                                        <div style={{ minWidth: 0 }}>
                                            <div className="omx-crumbs">
                                                <span
                                                    onClick={() => navigate(`/om/${site}`)}
                                                    className="cursor-pointer hover:underline"
                                                >
                                                    Operations &amp; Maintenance
                                                </span>
                                                <ChevronRight />
                                                <span>Checklists</span>
                                                <ChevronRight />
                                                <b>{selectedOption?.title}</b>
                                            </div>
                                            <span className="om-eyebrow">{selectedOption?.frequency}</span>
                                            <h1 className="om-h1" style={{ marginTop: 10 }}>{selectedOption?.title}</h1>
                                            <p className="om-lede" style={{ marginTop: 8, maxWidth: '62ch' }}>
                                                {selectedOption?.description}
                                            </p>
                                        </div>
                                        <div className="omx-pagehead__actions">
                                            <span className="omx-pill omx-pill--idle">
                                                <span className="omx-pill__dot" />
                                                In development
                                            </span>
                                        </div>
                                    </div>

                                    <div className="omx-card">
                                        <div className="omx-card__head">
                                            <span className="omx-card__title">
                                                <ClipboardCheck size={15} strokeWidth={1.8} />
                                                {selectedOption?.title} Form
                                            </span>
                                            <span className="omx-pill">
                                                <Calendar size={11} strokeWidth={2} />
                                                {selectedOption?.frequency}
                                            </span>
                                        </div>
                                        <div className="omx-card__body">
                                            <div className="omx-state">
                                                <span className="omx-state__icon">
                                                    <ClipboardCheck size={22} strokeWidth={1.6} />
                                                </span>
                                                <div className="omx-state__title">Form not yet available</div>
                                                <p className="omx-state__text">
                                                    This form is under development. The checklist will include all
                                                    necessary items for {selectedOption?.title.toLowerCase()}.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <div
                        className="om-page omx min-h-screen pb-6"
                        style={{ background: '#F6F8FB' }}
                    >
                        <PMCalendar />
                    </div>
                )}
            </div>
        </div>
    );
};

export default OMPage;
